// src/features/client/Client.js
import React, { useState, useEffect } from "react";
import "./Client.css";
import AddClients from "../client/AddClient"; // default export is AddClients
import { GrAddCircle } from "react-icons/gr";
import { BsSliders } from "react-icons/bs";
import config from "../../config";
import { IoSearchOutline } from "react-icons/io5";
import Header from "../../components/common/Header";


const Client = ({ setActivePage }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [showAddClient, setShowAddClient] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState({
    clientType: "",
    companySize: "",
    location: "",
    sortBy: "clientName",
    sortOrder: "ASC",
  });

  const [clientTypeOptions, setClientTypeOptions] = useState([]);
  const [companySizeOptions, setCompanySizeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);


  const [appliedFilters, setAppliedFilters] = useState({
    clientType: "",
    companySize: "",
    location: "",
    sortBy: "clientName",
    sortOrder: "ASC",
  });


  // Helper function to format client key with leading zeros
  const formatClientKey = (key) => {
    if (!key) return "";
    return "C" + String(key).padStart(3, "0");
  };

  const truncateText = (text, maxLength = 15) => {
    if (!text) return "N/A";
    const str = String(text);
    if (str.length > maxLength) {
      return str.slice(0, maxLength) + "...";
    }
    return str;
  };

  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1) Load user details
  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";

    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });
  }, []);

  // Fetch filter dropdown options
  const fetchFilterOptions = async (orgId, token) => {
    try {
      // Fetch Client Types
      const clientTypeUrl = `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${orgId}?configType=ClientType`;
      const clientTypeResponse = await fetch(clientTypeUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (clientTypeResponse.ok) {
        const clientTypeData = await clientTypeResponse.json();
        console.log("Client Type options:", clientTypeData);
        setClientTypeOptions(clientTypeData || []);
      }

      // Fetch Company Sizes
      const companySizeUrl = `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${orgId}?configType=CompanySize`;
      const companySizeResponse = await fetch(companySizeUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (companySizeResponse.ok) {
        const companySizeData = await companySizeResponse.json();
        console.log("Company Size options:", companySizeData);
        setCompanySizeOptions(companySizeData || []);
      }

      // Fetch Locations dynamically from client list
      try {
        const url = `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${orgId}?page=0&size=1000`;
        const locResponse = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        if (locResponse.ok) {
          const data = await locResponse.json();
          const clientsArray = data.body || [];
          const uniqueLocs = Array.from(new Set(clientsArray.map(c => c.location).filter(Boolean)));
          setLocationOptions(uniqueLocs);
        }
      } catch (err) {
        console.error("Error fetching locations from clients:", err);
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };


  const fetchClients = async (page = 0, filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (filters.clientType) params.append("clientType", filters.clientType);
      if (filters.companySize) params.append("companySize", filters.companySize);
      if (filters.location) params.append("location", filters.location);
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
      if (searchTerm) params.append("search", searchTerm);

      const url = `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${userDetails.orgId}?${params.toString()}`;
      console.log("Fetching client list:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch clients");

      const data = await response.json();
      console.log("Client list response:", data.body);
      setClients(data.body || []);
      setError(null);
    } catch (err) {
      console.error("Client fetch error:", err);
      setError("Could not load clients.");
    } finally {
      setLoading(false);
    }
  };
  // 2) Fetch list of clients
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    fetchClients(currentPage - 1, appliedFilters);
  }, [userDetails.token, userDetails.orgId, currentPage, appliedFilters, searchTerm]);


  // 3) When a row is clicked → call orgId/clientKey(or id) API
  const handleRowClick = async (client) => {
    console.log("Row clicked:", client);

    const { orgId, token } = userDetails;

    // IMPORTANT: what you use as key for the details API.
    // If your detail API uses `id`, use client.id.
    // If it uses some `clientKey`, use client.clientKey instead.
    const clientKeyOrId = client.id ?? client.clientKey;

    if (!clientKeyOrId) {
      alert("No id / clientKey found in this client row.");
      return;
    }

    const url = `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${orgId}/${clientKeyOrId}`;
    console.log("Fetching single client:", url);

    try {
      setLoading(true);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      console.log("Single client status:", response.status);

      if (!response.ok) throw new Error("Failed to fetch client details");

      const data = await response.json();
      console.log("Single client response:", data);

      // 👉 This object looks exactly like the JSON you pasted
      setSelectedClient(data);
      setShowAddClient(true);
      setError(null);
    } catch (err) {
      console.error("Single client fetch error:", err);
      setError("Unable to load client details.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddClientClick = () => {
    setSelectedClient(null); // fresh form
    setShowAddClient(true);
  };

  const filteredClients = clients.length > 0 ? clients : [];

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, startIndex + itemsPerPage);

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage) || 1;

  if (showAddClient) {
    return (
      <AddClients
        selectedClient={selectedClient} // 👈 IMPORTANT: pass it with this name
        onCancel={() => {
          setShowAddClient(false);
          setSelectedClient(null);
        }}
        fetchClients={fetchClients}
        onDelete={(clientToDelete) => {
          console.log("Delete clicked for:", clientToDelete);
          // you can plug your delete API here later
        }}
      />
    );
  }

  return (
    <div>
      <Header
        title="Client List"
        subtitle="Client Information"
        onProfileClick={() => setActivePage("Profile")}
      />

      <div className='table-container'>
        <div className='table-controls'>
          <div className="search-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <IoSearchOutline
              style={{
                position: 'absolute',
                left: '15px',
                fontSize: '15px',
                color: '#999'
              }}
            />
            <input
              type='text'
              className='search-input'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              /* Neenga image-la change panna andha width: 145px ippa inga irukku */
              style={{
                paddingLeft: '40px',
                width: '145px',
                height: '15px'
              }}
            />
          </div>

          <div className='button-container'>
            <button className='add-client-btn' onClick={handleAddClientClick}>
              <GrAddCircle />{" "}
              <span className='add-client-text'>Add Client</span>
            </button>
            <button
              className='filter-btn'
              onClick={() => {
                setShowFilter(true);
                fetchFilterOptions(userDetails.orgId, userDetails.token);
              }}
            >
              <BsSliders /> <span className='filter-btn-text'>Filter</span>
            </button>
            {showFilter && (
              <div
                className="rl-filter-overlay"
                onClick={() => setShowFilter(false)}
              >
                <div
                  className="rl-filter-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="rl-filter-header">Filter</div>

                  <div className="rl-filter-body">
                    <div className="rl-filter-field">
                      <label className="rl-filter-label">Client Type</label>
                      <select
                        className="rl-filter-select"
                        value={filterValues.clientType}
                        onChange={(e) =>
                          setFilterValues({ ...filterValues, clientType: e.target.value })
                        }
                      >
                        <option value=""></option>

                        {clientTypeOptions.map((option) => (
                          <option key={option.id} value={option.values}>
                            {option.values}
                          </option>
                        ))}
                      </select>

                    </div>

                    <div className="rl-filter-field">
                      <label className="rl-filter-label">Company Size</label>
                      <select
                        className="rl-filter-select"
                        value={filterValues.companySize}
                        onChange={(e) => setFilterValues({ ...filterValues, companySize: e.target.value })}
                      >
                        <option value=""></option>
                        {Array.isArray(companySizeOptions) &&
                          companySizeOptions.map((option) => (
                            <option key={option.id} value={option.values}>
                              {option.values}
                            </option>
                          ))}

                      </select>
                    </div>

                    <div className="rl-filter-field">
                      <label className="rl-filter-label">Location</label>
                      <select
                        className="rl-filter-select"
                        value={filterValues.location}
                        onChange={(e) => setFilterValues({ ...filterValues, location: e.target.value })}
                      >
                        <option value=""></option>
                        {locationOptions.map((loc, idx) => (
                          <option key={idx} value={loc}>
                            {loc}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="rl-filter-field">
                      <label className="rl-filter-label">Sort By</label>
                      <select
                        className="rl-filter-select"
                        value={filterValues.sortBy}
                        onChange={(e) => setFilterValues({ ...filterValues, sortBy: e.target.value })}
                      >
                        <option value="clientName">Name</option>
                        <option value="status">Status</option>
                      </select>
                    </div>


                  </div>

                  <div className="rl-filter-actions">
                    <button
                      className="rl-btn rl-btn-clear"
                      onClick={() => {
                        const resetFilters = {
                          clientType: "",
                          companySize: "",
                          location: "",
                          sortBy: "clientName",
                          sortOrder: "ASC",
                        };

                        setFilterValues(resetFilters);
                        setAppliedFilters(resetFilters);
                        setSearchTerm("");
                        setCurrentPage(1);
                        setShowFilter(false);
                      }}
                    >
                      Clear
                    </button>

                    <button
                      className="rl-btn rl-btn-apply"
                      onClick={() => {
                        setCurrentPage(1);
                        setAppliedFilters(filterValues); // 👈 only here API triggers
                        setShowFilter(false);
                      }}
                    >
                      Apply
                    </button>

                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        <table className='table'>
          <thead>
            <tr>
              <th className='th'>Client Name</th>
              <th className='th'>Location</th>
              <th className='th'>Client Key</th>
              <th className='th'>Client Type</th>
              <th className='th'>No.Employees</th>
              <th className='th'>Status</th>
            </tr>
          </thead>

          <tbody>
            {filteredClients.length === 0 ? (
              <tr className="empty-row">
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                  No clients found
                </td>
              </tr>
            ) : (
              paginatedClients.map((client) => (
                <tr
                  key={client.id ?? client.clientKey}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(client)}
                >
                  <td className="td" title={client.name || "N/A"}>{truncateText(client.name)}</td>
                  <td className="td" title={client.location || "N/A"}>{truncateText(client.location)}</td>
                  <td className="td" title={formatClientKey(client.clientKey)}>{truncateText(formatClientKey(client.clientKey))}</td>
                  <td className="td" title={client.clientType || "N/A"}>{truncateText(client.clientType)}</td>
                  <td className="td">{client.employeeCount || 0}</td>
                  <td className="td">
                    <span className={`status-badge ${client.status?.toLowerCase()}`} title={client.status || "Active"}>
                      {truncateText(client.status || "Active")}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className='table-footer'>
          <div className='record-count'>
            Showing {filteredClients.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, filteredClients.length)} of{" "}
            {filteredClients.length} entries
          </div>

          <div className='pagination'>
            <button
              className='pagination-button'
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              &lt;
            </button>

            {Array.from(
              { length: totalPages },
              (_, i) => (
                <button
                  key={i}
                  className={`pagination-button ${currentPage === i + 1 ? "active" : ""
                    }`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              )
            )}

            <button
              className='pagination-button'
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Client;
